<template>
	<div class="hotspot-content__container">
		<div class="hotspot-content__left-col">
			<div class="hotspot-content__media-container">
				<div class="hotspot-content__media-stage" :class="{ 'hotspot-content__media-stage--video': activeElement?.type === `video`}" v-if="isDesktopBreakpoint">
					<template v-if="activeElement?.type === `video`">
						<div class="embed-responsive embed-responsive-16by9">
							<div id="player" class="embed-responsive-item"></div>
						</div>
					</template>
					<template v-else>
						<img :src="activeElement?.passedValue" :alt="activeElement?.alt" class="hotspot-content__stage-image">
					</template>
				</div>
				<div class="hotspot-content__media-thumbs">
					<Swiper
							:modules="modules"
							:breakpoints="swiperOptions"
							:slides-per-view="3"
					        :space-between="10"
							:allowSlidePrev="true"
							:allowSlideNext="true"
					        ref="swiperInstance"
							:preloadImages="true"
					        :key="swiperKey"
							@init="swiperInit"
							@breakpoint="breakpointChange"
							@slideChange="slideChange"
							:navigation="{ prevEl: swiperLeft, nextEl: swiperRight }">
						<SwiperSlide v-for="(el, index) in combinedHotspotImagesAndVideos" :key="index" :class="{ 'active-element': index === activeSwiperSlideIndex }">
							<template v-if="el.hasOwnProperty('YouTubeID')">
								<template v-if="isDesktopBreakpoint">
									<span class="fa-solid fa-circle-play hotspot-content__play type-video" @click="setActiveElement(el, index)"></span>
									<img :src="`https://img.youtube.com/vi/${el.YouTubeID}/mqdefault.jpg`" :alt="`youtubevideo-${el.YouTubeID}`" class="hotspot-content__slider-image hotspot-content__slider-image--video-preview type-video" @click="setActiveElement(el, index)"/>
								</template>
								<template v-else>
									<div class="embed-responsive embed-responsive-16by9">
										<div id="mobileplayer" class="embed-responsive-item"></div>
									</div>
								</template>
							</template>
							<template v-else>
								<img :src="el.ImageUrl" :alt="el.AltText" class="hotspot-content__slider-image type-image" @click="setActiveElement(el, index)">
							</template>
						</SwiperSlide>
						<div class="hotspot-content__prev" ref="swiperLeft"><span class="fa-solid fa-chevron-left"></span></div>
						<div class="hotspot-content__next" ref="swiperRight"><span class="fa-solid fa-chevron-right"></span></div>
					</Swiper>
				</div>
			</div>
		</div>
		<div class="hotspot-content__right-col">
			<div class="hotspot-content__display-title animate__animated animate__fadeIn" v-if="hotspotItem.DisplayTitle" v-html="hotspotItem.DisplayTitle"></div>
			<div class="hotspot-content__logos-container animate__animated animate__fadeIn" v-if="hotspotItem?.TitleImages?.length">
				<div v-for="logo in hotspotItem?.TitleImages" class="hotspot-content__logos-col">
					<img  :src="logo.ImageUrl" :alt="logo.AltText"  class="hotspot-content__logo-image"/>
				</div>
			</div>
			<div class="hotspot-content__description animate__animated animate__fadeIn" v-if="hotspotItem?.Description" v-html="hotspotItem?.Description"></div>
			<div class="mt-4 animate__animated animate__fadeIn" v-if="hotspotItem?.CallToActionUrl">
				<div class="btn btn-primary" @click.prevent="goToUrl(hotspotItem.CallToActionUrl, hotspotItem.CallToActionNewWindow)">{{ hotspotItem.CallToActionText }}<span v-if="hotspotItem.CallToActionNewWindow" class="fas fa-external-link ml-1"></span></div>
			</div>
			<div class="hotspot-content__pager">
				<span class="fa-solid fa-chevron-left hotspot-content__pager-left" :class="{ 'disabled': index === 0 }" @click="goToPreviousIndex(index)"></span>{{ zeroInFront(index+1) }} of {{ zeroInFront(placementsLength) }}<span class="fa-solid fa-chevron-right hotspot-content__pager-right" :class="{ 'disabled': index + 1 >= placementsLength }" @click="goToNextIndex(index)"></span>
			</div>
		</div>
	</div>
</template>

<script lang="ts" setup>
import {onMounted, reactive, computed, ref, watch, nextTick} from 'vue';
import {Hotspot, HotspotPlacement} from "Interfaces/Interfaces";
import { Navigation } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/vue';

import { v4 as uuidv4 } from 'uuid';
declare let YT: any;
declare global {
	interface Window {
		onYouTubeIframeAPIReady?: () => void;
	}
}

const activeElement = reactive({
	type: '',
	passedValue: '',
	alt: ''
});

const modules = [Navigation];
const isDesktop = ref<boolean>(false);
const uniqueId = uuidv4();
const swiperOptions = {
	0: {
		slidesPerView: 1,
		spaceBetween: 10
	},
	768: {
		slidesPerView: 3,
		spaceBetween: 10
	},
};
const activeSwiperSlideIndex = ref<number>(0);
const swiperInstance = ref<any>(null);
const swiperLeft = ref(null);
const youtubePlayer = ref(null);
const swiperRight = ref(null);
const player = ref(null);
const swiperKey = ref(0);
const swiperInit = (swiper) => {
	swiperInstance.value = swiper;
};

const isDesktopBreakpoint = computed(() => {
	return isDesktop.value || matchMedia('(min-width: 768px)').matches;
});

const emit = defineEmits(['updateActiveHotspotIndex']);
const props = defineProps({
	placement: {
		type: Object as () => HotspotPlacement,
		default: () => ({})
	},
	hotspotItem: {
		type: Object as () => Hotspot,
		default: () => ({})
	},
	activeHotspotIndex: {
		type: Number,
		default: 0
	},
	placementsLength: {
		type: Number,
		default: 0
	},
	index: {
		type: Number,
		default: 0
	}
});

//Comps show a 0 in front of single digit numbers, this adds the zero if less than 10
const zeroInFront = (number) => {
	if (number < 10) {
		return '0' + number;
	}
	return number.toString(); // Convert to string if it's not a single digit
}

// This runs when pager previous button is clicked and sets it back 1 hotspot
const goToPreviousIndex = (index) => {
	if (index !== 0) {
		emit('updateActiveHotspotIndex', index - 1);
	}
};

const breakpointChange = (swiper) => {
	let windowWidth = window.innerWidth;
	isDesktop.value = windowWidth > 767;

	if(isDesktopBreakpoint.value) {
		setActiveElement(combinedHotspotImagesAndVideos.value[0], 0);
	} else {
		slideChange(swiper);
	}
}

// This runs when the page next button is clicked and sets the index forward one
const goToNextIndex = (index) => {
	if ((index + 1) < props.placementsLength) {
		emit('updateActiveHotspotIndex', index + 1);
	}
};

//combines videos and images into one array
const combinedHotspotImagesAndVideos = computed(() => {
	let combinedMediaElements = [];
	if(props?.hotspotItem?.HotspotVideos?.length) {
		combinedMediaElements = [ ...combinedMediaElements, ...props.hotspotItem?.HotspotVideos];
	}
	if(props?.hotspotItem?.HotspotImages?.length) {
		combinedMediaElements = [ ...combinedMediaElements, ...props.hotspotItem?.HotspotImages];
	}

	return combinedMediaElements;
});

//This function sets the active element when the swiper slide is changed at mobile
const slideChange = (swiper) => {
	if (!isDesktopBreakpoint.value) {
		let el = combinedHotspotImagesAndVideos.value[swiper.activeIndex];
		if (player.value) {
			// If there is an existing player instance, stop and destroy it
			player.value.destroy();
			player.value = null; // Ensure player is nullified
		}
		if(el.hasOwnProperty('YouTubeID')) {
			activeElement.type = 'video';
			activeElement.passedValue = el.YouTubeID;
			activeElement.alt = '';
			createYoutubePlayer(activeElement.passedValue, 'mobileplayer');
		} else {
			activeElement.type = 'image';
			activeElement.passedValue = el.ImageUrl;
			activeElement.alt = el.AltText;
		}

		activeSwiperSlideIndex.value = swiper.activeIndex;
	}
};


//This function sets the active element when a swiper slide is clicked at desktop
const setActiveElement = (el, index) => {
	if(isDesktopBreakpoint.value) {
		if(combinedHotspotImagesAndVideos.value[index].hasOwnProperty('YouTubeID')) {
			activeElement.type = 'video';
			activeElement.passedValue = el.YouTubeID;
			activeElement.alt = '';
			if (player.value) {
				// If there is an existing player instance, stop and destroy it
				player.value.destroy();
				player.value = null; // Ensure player is nullified
			}
			createYoutubePlayer(activeElement.passedValue, 'player');
		} else {
			activeElement.type = 'image';
			activeElement.passedValue = el.ImageUrl;
			activeElement.alt = el.AltText;
		}
		activeSwiperSlideIndex.value = index;
	}
}

//this makes the button go to the correct page when clicked keeping in mind new window or not
const goToUrl = (path, newWindow) => {
	if(newWindow === 'True') {
		window.open(path, '_blank');
	} else {
		window.location.href = path;
	}
};

//This function sets the intial active item (big picture/video) when the page loads and when its updated
const setInitialActiveElement = () => {
	if(combinedHotspotImagesAndVideos.value?.length) {
		if(combinedHotspotImagesAndVideos.value[0].hasOwnProperty('YouTubeID')) {
			activeElement.type = 'video';
			activeElement.passedValue = props.hotspotItem?.HotspotVideos[0]?.YouTubeID;
			activeElement.alt = '';

			//isDesktop is set to false initially and doesnt get rechecked until swiper initializes so use match media as well
			if(isDesktopBreakpoint.value) {
				createYoutubePlayer(activeElement.passedValue, 'player');
			} else {
				createYoutubePlayer(activeElement.passedValue, 'mobileplayer');
			}
		} else {
			activeElement.type = 'image';
			activeElement.passedValue = props.hotspotItem?.HotspotImages[0]?.ImageUrl;
			activeElement.alt = props.hotspotItem?.HotspotImages[0]?.AltText;
		}
		activeSwiperSlideIndex.value = 0;
	}
}

// Check if the API script has already been loaded, if not inject it in
const injectYoutubePlayerAPIScript = () => {
	if (typeof YT === 'undefined' || typeof YT.Player === 'undefined') {
		// Load the IFrame Player API code asynchronously if not loaded already
		let tag = document.createElement('script');
		tag.src = "https://www.youtube.com/iframe_api";
		let firstScriptTag = document.getElementsByTagName('script')[0];
		firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
	}
}

watch(() => props.activeHotspotIndex, (oldValue, newValue) => {
	swiperKey.value++;
});

const playVideo = (event) => {
	player.value.playVideo();
}


const createYoutubePlayer = (videoId:string, playerId:string) => {
	//@ts-ignore

	nextTick(() => {
		player.value = new YT.Player(`${playerId}`, {
			height: '390',
			width: '640',
			videoId: videoId,
			playerVars: {
				'playsinline': 1,
				'controls': 0,
				'showinfo': 0
			},
			events: {
				'onReady': playVideo,
			}
		});
	})
}

onMounted(() => {
	injectYoutubePlayerAPIScript();
});
</script>
